/**
 * On-demand loader for Mapbox GL JS (script + stylesheet).
 *
 * mapbox-gl.js is ~230 KB gzipped (≈1 MB parsed). It used to ship in <head> of EVERY page via
 * IncludeScripts — mobile Lighthouse counted it as the biggest unused script on the home page, the
 * city pages and the listing page, none of which shows a map above the fold. Every consumer that
 * actually draws a map now awaits `loadMapboxGl()` right before it creates one (search map on
 * mount, listing map when it scrolls near the viewport, …). Location search does not need the
 * library at all: GeocoderMapbox talks to the Geocoding HTTP API with the access token only.
 *
 * CSP: the script is injected with a plain src from api.mapbox.com, which `script-src` allows by
 * host (server/csp.js) — no nonce needed, the policy has no 'strict-dynamic'. The stylesheet is
 * allowed by `style-src api.mapbox.com`.
 *
 * The access token comes from the app configuration (hosted map asset first, env fallback), which
 * only IncludeScripts sees app-wide. It registers the token here during render, and the loader
 * attaches it to `window.mapboxgl` before resolving, so every `new mapboxgl.Map()` after an
 * `await loadMapboxGl()` has a token.
 */
import { forgetScript, loadScriptOnce } from './loadScript';

export const MAPBOX_GL_VERSION = 'v3.7.0';
// License information for v3.7.0 of the mapbox-gl-js library:
// https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt
export const MAPBOX_GL_JS_SRC = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_VERSION}/mapbox-gl.js`;
export const MAPBOX_GL_CSS_SRC = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_VERSION}/mapbox-gl.css`;
export const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
export const MAPBOX_CSS_ID = 'mapbox_GL_CSS';

let registeredAccessToken = null;
let glPromise = null;

/**
 * Attach the access token to the loaded library. A token that is already set is never
 * overwritten: mapbox-gl copies it into every Map created from it, so a later swap would leave
 * mounted maps on the old token while new ones use the new one.
 *
 * @returns {Object|null} window.mapboxgl, or null if the library is not loaded
 */
const assignAccessToken = () => {
  const mapboxgl = typeof window !== 'undefined' ? window.mapboxgl : null;
  if (!mapboxgl) {
    return null;
  }
  if (registeredAccessToken && !mapboxgl.accessToken) {
    mapboxgl.accessToken = registeredAccessToken;
  }
  return mapboxgl;
};

/**
 * Register the configured Mapbox access token (called by IncludeScripts during render, on the
 * server as well — harmless there, nothing reads it).
 *
 * @param {string?} token
 */
export const setMapboxAccessToken = token => {
  if (token) {
    registeredAccessToken = token;
    assignAccessToken();
  }
};

/**
 * @returns {string|null} the access token registered from the app configuration
 */
export const getMapboxAccessToken = () => registeredAccessToken;

/**
 * @returns {boolean} true if Mapbox GL JS has executed on this page
 */
export const isMapboxGlLoaded = () => typeof window !== 'undefined' && !!window.mapboxgl;

// Resolves when the stylesheet has loaded — or failed: a map without its CSS still works (the
// controls just look off), so a blocked stylesheet must not take the map down with it.
const loadStylesheet = () =>
  new Promise(resolve => {
    const existing =
      document.getElementById(MAPBOX_CSS_ID) ||
      document.querySelector(`link[href="${MAPBOX_GL_CSS_SRC}"]`);
    if (existing) {
      // Already in the document (an earlier load, or a server-rendered tag): there is no reliable
      // way to tell whether it finished, and waiting on a 'load' that already fired would hang.
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.id = MAPBOX_CSS_ID;
    link.rel = 'stylesheet';
    link.href = MAPBOX_GL_CSS_SRC;
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });

/**
 * Load Mapbox GL JS and its stylesheet once per page. Concurrent and later callers share the same
 * promise. A failed load (network, content blocker) is forgotten, so the next call tries again.
 *
 * @returns {Promise<Object>} resolves with window.mapboxgl, access token attached
 */
export const loadMapboxGl = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Mapbox GL cannot be loaded on the server'));
  }
  if (window.mapboxgl) {
    // Already executed (an earlier load on this page). Make sure the stylesheet is there too —
    // mapbox-gl warns about missing CSS — but don't hold the map back for it.
    loadStylesheet();
    return Promise.resolve(assignAccessToken());
  }
  if (glPromise) {
    return glPromise;
  }

  glPromise = Promise.all([
    loadScriptOnce(MAPBOX_GL_JS_SRC, {
      id: MAPBOX_SCRIPT_ID,
      async: true,
      crossorigin: 'anonymous',
    }),
    loadStylesheet(),
  ])
    .then(() => {
      const mapboxgl = assignAccessToken();
      if (!mapboxgl) {
        // The request "succeeded" but defined nothing (e.g. a blocker answered with an empty
        // file). Drop the dud so a later call fetches it again.
        forgetScript(MAPBOX_GL_JS_SRC);
        throw new Error('Mapbox GL JS loaded but window.mapboxgl is missing');
      }
      return mapboxgl;
    })
    .catch(e => {
      glPromise = null;
      throw e;
    });

  return glPromise;
};

/**
 * Test helper: forget the cached promise and the registered token.
 */
export const resetMapboxLoaderForTests = () => {
  glPromise = null;
  registeredAccessToken = null;
  forgetScript(MAPBOX_GL_JS_SRC);
};
